import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import React, { useCallback } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_SPLIT_RATIO,
  useOrderInboundsStore,
} from '@/stores/order-inbounds-store';
import { DragHandle } from '@/components/drag-handle';
import {
  GestureUpdateEvent,
  PanGestureHandlerEventPayload,
} from 'react-native-gesture-handler';
import { LayoutChangeEvent, StyleProp, View, ViewStyle } from 'react-native';
import { SCREEN_PADDING_SMALL, SCREEN_PADDING_XS } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';

export const ORIGINAL_INBOUND_CONTENT_MIN_WIDTH = 296;

export const DETAIL_CONTENT_MIN_WIDTH = 375;
export const DETAIL_CONTENT_LEGIBLE_MIN_WIDTH = 610;

export interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  detailContent?: React.ReactNode;
  onResize?: (input: { newDetailContentWidth: number }) => void;
  originalInboundContent?: React.ReactNode;
  originalInboundHeader?: React.ReactNode;
  panelStyle?: StyleProp<ViewStyle>;
}

export const InboundDetailSplitContainer: React.FC<Props> = ({
  containerStyle,
  detailContent,
  originalInboundContent,
  panelStyle,
  onResize,
  originalInboundHeader,
}) => {
  const [styles] = useStyles();
  const { isSmallLayout, isSmallTabletLayout, isLargeLayout, isMediumLayout } =
    useLayoutContext();
  const hasResize = !isSmallLayout;
  const savedSplitRatio = useOrderInboundsStore(
    (state) => state.savedSplitRatio,
  );
  const setSavedSplitRatio = useOrderInboundsStore(
    (state) => state.actions.setSavedSplitRatio,
  );
  const initialSplitRatio = hasResize ? savedSplitRatio : DEFAULT_SPLIT_RATIO;

  const splitContainerLayoutWidth = useSharedValue(0);
  const initialSplitRatioInner = useSharedValue(initialSplitRatio);
  const splitRatio = useSharedValue(initialSplitRatio);

  const onContainerLayout = useCallback(
    (event: LayoutChangeEvent) => {
      splitContainerLayoutWidth.value = event.nativeEvent.layout.width;
    },
    [splitContainerLayoutWidth],
  );

  // Callbacks that will run on JS thread
  const handleDragEnd = useCallback(
    (finalSplitRatio: number) => {
      setSavedSplitRatio(finalSplitRatio);

      if (onResize) {
        const originalInboundContentWidth =
          splitContainerLayoutWidth.value * finalSplitRatio;

        onResize({
          newDetailContentWidth:
            splitContainerLayoutWidth.value - originalInboundContentWidth,
        });
      }
    },
    [onResize, setSavedSplitRatio, splitContainerLayoutWidth],
  );

  const handleGestureStart = useCallback(() => {
    'worklet';
    // Without this, the handle will be reset to the passed in initialSplitRatio. Doing this will keep the position
    // of the drag handle after trying to move it after the first time.
    initialSplitRatioInner.value = splitRatio.value;
  }, [initialSplitRatioInner, splitRatio]);

  const handleGestureUpdate = useCallback(
    (event: GestureUpdateEvent<PanGestureHandlerEventPayload>) => {
      'worklet';
      if (splitContainerLayoutWidth.value <= 0) {
        return;
      }

      const currOriginalInboundContentWidth =
        splitContainerLayoutWidth.value * initialSplitRatioInner.value;

      const newOriginalInboundContentWidth = Math.max(
        ORIGINAL_INBOUND_CONTENT_MIN_WIDTH,
        currOriginalInboundContentWidth + event.translationX,
      );

      // We wanna clamp this value so that we respect the minimum widths for each panel
      splitRatio.value = Math.min(
        Math.max(
          newOriginalInboundContentWidth / splitContainerLayoutWidth.value,
          ORIGINAL_INBOUND_CONTENT_MIN_WIDTH / splitContainerLayoutWidth.value,
        ),
        (splitContainerLayoutWidth.value - DETAIL_CONTENT_MIN_WIDTH) /
          splitContainerLayoutWidth.value,
      );
    },
    [initialSplitRatioInner, splitContainerLayoutWidth, splitRatio],
  );

  const handleGestureEnd = useCallback(() => {
    'worklet';
    runOnJS(handleDragEnd)(splitRatio.value);
  }, [handleDragEnd, splitRatio]);

  const animatedLeftPanelStyle = useAnimatedStyle(() => {
    const width = splitContainerLayoutWidth.value * splitRatio.value;
    return {
      width,
    };
  }, [splitRatio, splitContainerLayoutWidth]);

  const animatedRightPanelStyle = useAnimatedStyle(() => {
    const width = splitContainerLayoutWidth.value * (1 - splitRatio.value);
    return {
      width,
    };
  }, [splitRatio, splitContainerLayoutWidth]);

  const animatedDragHandleStyle = useAnimatedStyle(() => {
    const leftPanelWidth = splitContainerLayoutWidth.value * splitRatio.value;
    return {
      left: leftPanelWidth - 2.5, // Center the 5px handle on the boundary
    };
  }, [splitRatio, splitContainerLayoutWidth]);

  return (
    <View style={styles.outerWrapper}>
      <View
        onLayout={onContainerLayout}
        style={[
          styles.contentWrapperLarge,
          isSmallTabletLayout || hasResize ? null : styles.contentGap,
          containerStyle,
        ]}
      >
        {isSmallLayout ? (
          detailContent
        ) : (
          <>
            <Animated.View
              style={[
                styles.originalInboundsWrapper,
                isSmallTabletLayout || isMediumLayout || isLargeLayout
                  ? styles.originalInboundsWrapperLarge
                  : null,
                panelStyle,
                animatedLeftPanelStyle,
              ]}
              testID="original-inbound-content-panel"
            >
              {originalInboundHeader ? (
                <View style={styles.originalInboundHeaderWrapper}>
                  {originalInboundHeader}
                </View>
              ) : null}
              {originalInboundContent}
            </Animated.View>

            <DragHandle
              onGestureEnd={handleGestureEnd}
              onGestureStart={handleGestureStart}
              onGestureUpdate={handleGestureUpdate}
              style={[styles.dragHandleAbsolute, animatedDragHandleStyle]}
              useWorklets
            />

            <Animated.View
              style={[
                styles.listWrapper,
                isLargeLayout ? styles.listWrapperLarge : null,
                animatedRightPanelStyle,
              ]}
              testID="inbound-detail-list-panel"
            >
              {detailContent}
            </Animated.View>
          </>
        )}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  contentGap: {
    gap: SCREEN_PADDING_XS,
  },
  contentWrapperLarge: {
    flex: 1,
    flexDirection: 'row',
    position: 'relative',
  },
  dragHandleAbsolute: {
    position: 'absolute',
  },
  listWrapper: {
    backgroundColor: theme.colors.white,
  },
  listWrapperLarge: {
    minWidth: DETAIL_CONTENT_MIN_WIDTH,
  },
  originalInboundHeaderWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    height: 72,
    marginLeft: SCREEN_PADDING_SMALL,
    paddingBottom: 12,
    paddingTop: 12,
  },
  originalInboundsWrapper: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: 8,
  },
  originalInboundsWrapperLarge: {
    borderRadius: 0,
    minWidth: ORIGINAL_INBOUND_CONTENT_MIN_WIDTH,
  },
  outerWrapper: {
    flex: 1,
  },
}));
